import {
  AllCommunityModule,
  CellEditingStoppedEvent,
  CellValueChangedEvent,
  ColDef,
  ColGroupDef,
  GridApi,
  GridReadyEvent,
  ModuleRegistry,
  themeQuartz,
} from "ag-grid-community";
import { AllEnterpriseModule, LicenseManager } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { forwardRef, useCallback, useMemo, useRef, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import DeliverableService from "../../../features/DeliverableView/services/DeliverableService";
import CoaService from "../../../features/CoaView/services/CoaService";
import TemplateService from "../../../features/TemplateView/services/TemplateService";
import { AgGridContainer, AgGridWrapper } from "./AgGridComponent.styles";
import { AgGridComponentProps } from "./AgGridComponent.types";
import { AgGridHandle } from "./AgGridContext";
import { handleCellValidation } from "./AgGridUtils";
import { ENV } from "../../../config";
import { useAgGridStyles } from "./hooks/useAgGridStyles";
import { useColumnDefs } from "./hooks/useColumnDefs";
import { useAgGridFetch } from "./hooks/useAgGridFetch";
import { useAgGridActions } from "./hooks/useAgGridActions";
import { useDeleteRow } from "./hooks/useDeleteRow";
import { useColumnSizing } from "./hooks/useColumnSizing";

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);
LicenseManager.setLicenseKey(ENV.AG_GRID_API_KEY || "");

const agGridTheme = themeQuartz.withParams({
  rowBorder: { style: "solid", width: 1, color: "rgba(255, 255, 255, 0.3)" },
});

export const AgGridComponent = forwardRef<AgGridHandle, AgGridComponentProps>(
  (
    {
      rowData: externalRowData,
      columnDefs: externalColumnDefs,
      defaultColDef,
      pagination = false,
      paginationPageSize,
      paginationPageSizeSelector,
      rowSelection = "single",
      height,
      onRowClicked,
      onCellValueChanged,
      onCellEditingStopped,
      onGridReady,
      loading: externalLoading = false,
      gridOptions,
    },
    ref,
  ) => {
    const { deliverableId, sheetId } = useParams<{
      deliverableId: string;
      sheetId: string;
    }>();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const activeTab = searchParams.get("activeTab");

    const [isEditMode, setIsEditMode] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const gridApiRef = useRef<GridApi | null>(null);
    const deliverableService = useRef(new DeliverableService()).current;
    const coaService = useRef(new CoaService()).current;
    const templateService = useRef(new TemplateService()).current;
    const isSelfFetching = !externalRowData && !externalColumnDefs;

    useAgGridStyles();

    const { transformColumnDefs, mergeColumnDefsWithMeta, mergedDefaultColDef } =
      useColumnDefs({ isEditMode, defaultColDef });

    const { internalRowData, internalColumnDefs, internalLoading, fetchData } = useAgGridFetch({
      deliverableId,
      sheetId,
      pathname,
      isSelfFetching,
      deliverableService,
      coaService,
      templateService,
      isEditMode,
      transformColumnDefs,
    });

    useAgGridActions({
      ref,
      gridApiRef,
      deliverableId,
      sheetId,
      pathname,
      activeTab,
      fetchData,
      deliverableService,
      coaService,
      templateService,
      setIsEditMode,
      setSearchValue,
    });

    const rowData = isSelfFetching ? internalRowData : externalRowData;
    const columnDefs = isSelfFetching ? internalColumnDefs : externalColumnDefs;
    const isLoading = isSelfFetching ? internalLoading : externalLoading;

    const mergedColumnDefs = useMemo(
      () => mergeColumnDefsWithMeta(columnDefs as (ColDef | ColGroupDef)[] | undefined),
      [columnDefs, mergeColumnDefsWithMeta],
    );

    const deleteRow = useDeleteRow(isEditMode);

    const { fitOrAutoSizeColumns, onColumnRowGroupChanged } = useColumnSizing({
      gridApiRef,
      rowData,
      columnDefs,
    });

    const handleGridReady = useCallback(
      (event: GridReadyEvent) => {
        gridApiRef.current = event.api;
        onGridReady?.(event);
      },
      [onGridReady],
    );

    const handleGridCellValueChanged = useCallback(
      (event: CellValueChangedEvent<any>) => {
        handleCellValidation(event);
        onCellValueChanged?.(event);
      },
      [onCellValueChanged],
    );

    const handleGridCellEditingStopped = useCallback(
      (event: CellEditingStoppedEvent<any>) => {
        handleCellValidation(event);
        onCellEditingStopped?.(event);
      },
      [onCellEditingStopped],
    );

    return (
      <AgGridWrapper>
        <AgGridContainer height={height}>
          <AgGridReact
            theme={agGridTheme}
            rowData={rowData}
            rowHeight={32}
            columnDefs={mergedColumnDefs}
            defaultColDef={mergedDefaultColDef}
            rowSelection={rowSelection}
            onRowClicked={onRowClicked}
            onGridReady={handleGridReady}
            rowGroupPanelShow={"always"}
            onCellValueChanged={handleGridCellValueChanged}
            onCellEditingStopped={handleGridCellEditingStopped}
            loading={isLoading}
            quickFilterText={searchValue}
            tooltipShowDelay={100}
            tooltipShowMode={"whenTruncated"}
            tooltipInteraction={true}
            suppressFieldDotNotation={true}
            pagination={pagination}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={paginationPageSizeSelector}
            suppressMovableColumns={true}
            suppressDragLeaveHidesColumns={true}
            onColumnRowGroupChanged={onColumnRowGroupChanged}
            cellSelection={true}
            rowClassRules={{
              "custom-group-row": (params) => params.node.group === true,
            }}
            onFirstDataRendered={(params) => fitOrAutoSizeColumns(params.api)}
            onDisplayedColumnsChanged={(params) => fitOrAutoSizeColumns(params.api)}
            getContextMenuItems={deleteRow}
            {...gridOptions}
          />
        </AgGridContainer>
      </AgGridWrapper>
    );
  },
);
